import { useEffect, useState, useMemo, useCallback } from 'react';
import { useProjectViewStore } from '@/stores/projectView';
import type { Collector, PhotoItem } from '@/types/odoo';
import {
  fetchSubcontractors,
  fetchPoStatusForProject,
} from '@/api/onderaannemers';
import type { SubcontractorInstaller } from '@/api/onderaannemers';
import {
  listProjectMeerwerken,
  MEERWERK_TYPE_LABELS,
  type Meerwerk,
} from '@/api/meerwerken';
import { AdminMeerwerkForm } from './AdminMeerwerkForm';
import { useAuthStore } from '@/stores/auth';
import { listCollectorPhotos } from '@/api/tasks';
import { getCollectorMeta } from '@/utils/collectors';
import { PhotoLightbox } from '@/components/ui/PhotoLightbox';
import { PhotoGridThumb } from '@/components/ui/PhotoGridThumb';
import { isPdfPhoto } from '@/utils/photoFiles';
import { GeenPrijsLabel } from '@/components/ui/GeenPrijsLabel';

// NOTE: The PO write path (select collectors → create inkooporder) moved to the
// OA-dagoverzicht, which appends to the vendor's open rolling PO (append-or-create).
// This tab is now READ-ONLY — it shows collectors, meerwerken and their PO status.

interface InkoopordersTabProps {
  projectId: number;
  projectName: string;
}

interface PoStatusEntry {
  hasOrder: true;
  orderId: number;
  orderName: string;
}

interface DateGroup {
  dateKey: string; // YYYY-MM-DD or '' for no date
  collectors: Collector[];
}

interface InstallerGroup {
  installerId: number;
  installerName: string;
  vendorName: string;
  collectors: Collector[];
  dateGroups: DateGroup[];
  pendingMeerwerken: Meerwerk[];
}

function formatOpDrukDate(dateKey: string): string {
  if (!dateKey) return 'Geen datum';
  return `${dateKey.slice(8, 10)}-${dateKey.slice(5, 7)}-${dateKey.slice(0, 4)}`;
}

export function InkoopordersTab({ projectId, projectName }: InkoopordersTabProps) {
  const isAdmin = useAuthStore((s) => s.hasRole('admin'));
  const collectors = useProjectViewStore((s) => s.collectors);
  const collectorLoading = useProjectViewStore((s) => s.collectorLoading);
  const collectorError = useProjectViewStore((s) => s.collectorError);
  const collectorProjectId = useProjectViewStore((s) => s.collectorProjectId);
  const fetchCollectoren = useProjectViewStore((s) => s.fetchCollectoren);

  const [installers, setInstallers] = useState<SubcontractorInstaller[]>([]);
  const [installersLoading, setInstallersLoading] = useState(false);
  const [poStatus, setPoStatus] = useState<Record<number, PoStatusEntry>>({});
  const [poStatusLoading, setPoStatusLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Meerwerken — both pending and on_po so on_po records show their PO link.
  const [pendingMeerwerken, setPendingMeerwerken] = useState<Meerwerk[]>([]);

  const refreshMeerwerken = useCallback(async () => {
    try {
      const data = await listProjectMeerwerken(projectId);
      setPendingMeerwerken(data.filter((m) => m.status === 'pending' || m.status === 'on_po'));
    } catch {
      // non-blocking
    }
  }, [projectId]);

  useEffect(() => {
    refreshMeerwerken();
  }, [refreshMeerwerken]);

  // Load collectors if not already loaded for this project
  useEffect(() => {
    if (collectorProjectId !== projectId) {
      fetchCollectoren(projectId);
    }
  }, [projectId, collectorProjectId, fetchCollectoren]);

  // Fetch installers on mount
  useEffect(() => {
    let cancelled = false;
    setInstallersLoading(true);
    fetchSubcontractors()
      .then((data) => {
        if (!cancelled) setInstallers(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Installateurs laden mislukt');
      })
      .finally(() => {
        if (!cancelled) setInstallersLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Fetch PO status for this project
  const refreshPoStatus = useCallback(() => {
    setPoStatusLoading(true);
    fetchPoStatusForProject(projectId)
      .then((data) => setPoStatus(data))
      .catch(() => {})
      .finally(() => setPoStatusLoading(false));
  }, [projectId]);

  useEffect(() => {
    refreshPoStatus();
  }, [refreshPoStatus]);

  // Auto-dismiss error
  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(null), 15000);
    return () => clearTimeout(timer);
  }, [error]);

  // Group collectors by installer (subcontractors only)
  const installerGroups = useMemo(() => {
    if (installers.length === 0) return [];

    const installerMap = new Map<number, SubcontractorInstaller>();
    for (const inst of installers) {
      installerMap.set(inst.id, inst);
    }

    const groups = new Map<number, InstallerGroup>();
    const ensureGroup = (instId: number): InstallerGroup | null => {
      const inst = installerMap.get(instId);
      if (!inst) return null;
      let g = groups.get(instId);
      if (!g) {
        g = {
          installerId: instId,
          installerName: inst.name,
          vendorName: inst.vendorName,
          collectors: [],
          dateGroups: [],
          pendingMeerwerken: [],
        };
        groups.set(instId, g);
      }
      return g;
    };

    if (collectors) {
      for (const c of collectors) {
        const instIds = c.x_studio_collector_installateur ?? [];
        for (const instId of instIds) {
          const g = ensureGroup(instId);
          if (!g) continue;
          g.collectors.push(c);
        }
      }
    }

    // Attach pending/on_po meerwerken to their subcontractor — also creates groups
    // for installers who have only meerwerken on this project.
    for (const m of pendingMeerwerken) {
      if (!m.subcontractor_id) continue;
      const g = ensureGroup(m.subcontractor_id);
      if (!g) continue;
      g.pendingMeerwerken.push(m);
    }

    // Sort collectors within each group: newest op_druk_datum first; build date sub-groups.
    for (const group of groups.values()) {
      group.collectors.sort((a, b) => {
        const dateA = a.x_studio_collector_op_druk_datum || '';
        const dateB = b.x_studio_collector_op_druk_datum || '';
        return dateB.localeCompare(dateA); // newest first
      });

      const dateMap = new Map<string, Collector[]>();
      for (const c of group.collectors) {
        const raw = c.x_studio_collector_op_druk_datum;
        const key = typeof raw === 'string' && raw.length >= 10 ? raw.slice(0, 10) : '';
        if (!dateMap.has(key)) dateMap.set(key, []);
        dateMap.get(key)!.push(c);
      }
      const sortedKeys = [...dateMap.keys()].sort((a, b) => {
        if (!a && !b) return 0;
        if (!a) return 1;
        if (!b) return -1;
        return b.localeCompare(a);
      });
      group.dateGroups = sortedKeys.map((dateKey) => {
        const cols = dateMap.get(dateKey)!;
        cols.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
        return { dateKey, collectors: cols };
      });
    }

    return [...groups.values()].sort((a, b) => a.installerName.localeCompare(b.installerName));
  }, [collectors, installers, pendingMeerwerken]);

  const isLoading = collectorLoading || installersLoading;

  return (
    <div>
      {/* Header */}
      <div className="mb-5">
        <h2 className="text-lg font-extrabold text-td-navy">Inkooporders</h2>
        <p className="text-sm text-td-muted mt-0.5">
          Overzicht van collectoren, meerwerken en hun inkooporder-status.
        </p>
      </div>

      {/* Write path moved to the OA-dagoverzicht (rolling PO append-or-create). */}
      <div className="mb-4 rounded-xl bg-td-blue/5 border border-td-blue/20 px-4 py-3" role="note">
        <p className="text-sm text-td-navy font-medium">
          PO’s worden nu via het OA-dagoverzicht aangemaakt.
        </p>
        <p className="text-xs text-td-muted mt-0.5">
          Deze pagina toont enkel de status; selecteren en aanmaken gebeurt centraal in het dagoverzicht.
        </p>
      </div>

      {/* Error banner */}
      {(error || collectorError) && (
        <div className="mb-4 bg-red-50 text-red-600 p-4 rounded-xl" role="alert">
          <p className="text-sm font-semibold whitespace-pre-line">{error || collectorError}</p>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
            <span className="sr-only">Laden...</span>
          </div>
          <p className="text-td-muted text-sm mt-3">Gegevens laden...</p>
        </div>
      )}

      {/* No subcontractor collectors */}
      {!isLoading && installerGroups.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <p className="text-td-muted text-sm">Geen collectoren of meerwerken voor dit project.</p>
        </div>
      )}

      {/* Installer groups with collectors */}
      {!isLoading && installerGroups.length > 0 && (
        <div className="space-y-6">
          {installerGroups.map((group) => (
            <InstallerGroupSection
              key={group.installerId}
              group={group}
              projectId={projectId}
              projectName={projectName}
              isAdmin={isAdmin}
              poStatus={poStatus}
              poStatusLoading={poStatusLoading}
              refreshMeerwerken={refreshMeerwerken}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Installer group section ─── */

interface InstallerGroupSectionProps {
  group: InstallerGroup;
  projectId: number;
  projectName: string;
  isAdmin: boolean;
  poStatus: Record<number, PoStatusEntry>;
  poStatusLoading: boolean;
  refreshMeerwerken: () => Promise<void>;
}

function InstallerGroupSection({
  group,
  projectId,
  projectName,
  isAdmin,
  poStatus,
  poStatusLoading,
  refreshMeerwerken,
}: InstallerGroupSectionProps) {
  const [showMeerwerkForm, setShowMeerwerkForm] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Installer header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200 gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-bold text-td-navy truncate" title={group.installerName}>{group.installerName}</h3>
          <p className="text-xs text-td-muted truncate" title={group.vendorName}>
            {group.vendorName}
            {group.collectors.length > 0 && ` · ${group.collectors.length} collectoren`}
          </p>
        </div>
        {isAdmin && (
          <button
            type="button"
            onClick={() => setShowMeerwerkForm((v) => !v)}
            className="shrink-0 px-3 py-1.5 text-xs font-bold text-td-blue border border-td-blue/30 rounded-lg hover:bg-td-blue/5 transition-colors"
          >
            {showMeerwerkForm ? 'Annuleren' : 'Meerwerk toevoegen'}
          </button>
        )}
      </div>

      {/* Admin inline meerwerk create form */}
      {isAdmin && showMeerwerkForm && (
        <AdminMeerwerkForm
          projectId={projectId}
          subcontractorId={group.installerId}
          onCreated={async () => {
            setShowMeerwerkForm(false);
            await refreshMeerwerken();
          }}
          onCancel={() => setShowMeerwerkForm(false)}
        />
      )}

      {/* Collectors — sub-grouped by op-druk datum (header only when >1 date) */}
      {group.dateGroups.map((dg) => (
        <div key={dg.dateKey || 'no-date'}>
          {group.dateGroups.length > 1 && (
            <div className="px-4 py-1.5 bg-gray-50/60 border-t border-gray-100">
              <p className="text-[11px] font-bold uppercase tracking-wide text-td-muted">
                Op druk · {formatOpDrukDate(dg.dateKey)}
              </p>
            </div>
          )}
          {dg.collectors.map((collector) => (
            <CollectorPoRow
              key={collector.id}
              collector={collector}
              projectName={projectName}
              poEntry={poStatus[collector.id]}
              poStatusLoading={poStatusLoading}
            />
          ))}
        </div>
      ))}

      {/* Meerwerken — read-only; on_po shows its PO link */}
      {group.pendingMeerwerken.length > 0 && (
        <div className="border-t border-gray-100">
          <div className="px-4 py-1.5 bg-amber-50/60">
            <p className="text-[11px] font-bold uppercase tracking-wide text-amber-800">
              Meerwerken · {group.pendingMeerwerken.length}
            </p>
          </div>
          <ul className="divide-y divide-gray-100">
            {group.pendingMeerwerken.map((m) => {
              const isOnPo = m.status === 'on_po';
              // null = no price (a km line the server would refuse): no amount, no total.
              const effectivePrice = m.effective_unit_price;
              const total = effectivePrice === null ? null : effectivePrice * m.quantity;
              return (
                <li
                  key={m.id}
                  className={`px-4 py-2 flex items-start gap-3 ${isOnPo ? 'bg-gray-50/70 opacity-60' : ''}`}
                  title={isOnPo ? `Al verwerkt op ${m.purchase_order_name ?? 'een eerdere PO'}` : undefined}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm font-semibold truncate ${isOnPo ? 'text-td-muted line-through' : 'text-td-navy'}`}>{m.name}</span>
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-td-blue/10 text-td-blue">
                        {MEERWERK_TYPE_LABELS[m.type] ?? m.type}
                      </span>
                      <span className="text-xs text-td-muted">{m.date}</span>
                      {isOnPo && m.purchase_order_name && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-green-100 text-green-800">
                          PO {m.purchase_order_name}
                        </span>
                      )}
                      {!isOnPo && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">
                          Nog te factureren
                        </span>
                      )}
                    </div>
                    {m.note && <p className="text-xs text-td-muted mt-0.5">{m.note}</p>}
                  </div>
                  <div className="text-right text-xs text-td-muted shrink-0">
                    {effectivePrice === null ? (
                      <>
                        <div>{m.quantity}{m.unit === 'km' ? ' km' : ''}</div>
                        <GeenPrijsLabel reason={m.price_unavailable_reason} className="block max-w-[16rem]" />
                      </>
                    ) : (
                      <div>{m.quantity} × €{effectivePrice.toFixed(2)}</div>
                    )}
                    {total !== null && total > 0 && <div className="font-semibold text-td-navy">€{total.toFixed(2)}</div>}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ─── Collector row with PO status (read-only) ─── */

interface CollectorPoRowProps {
  collector: Collector;
  projectName: string;
  poEntry: PoStatusEntry | undefined;
  poStatusLoading: boolean;
}

function CollectorPoRow({
  collector,
  projectName,
  poEntry,
  poStatusLoading,
}: CollectorPoRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const hasPo = !!poEntry;
  const hasKringen = (collector.kringen?.length ?? 0) > 0;
  const kringenCount = collector.kringen?.length ?? 0;
  const blokName = collector.x_studio_blok && Array.isArray(collector.x_studio_blok)
    ? collector.x_studio_blok[1]
    : null;

  // Load photos when expanded
  useEffect(() => {
    if (!expanded) return;
    if (photos.length > 0) return; // already loaded
    let cancelled = false;
    setPhotosLoading(true);
    const meta = getCollectorMeta(collector, projectName);
    listCollectorPhotos(collector.id, meta)
      .then((r) => { if (!cancelled) setPhotos(r.photos); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setPhotosLoading(false); });
    return () => { cancelled = true; };
  }, [expanded, collector, projectName, photos.length]);

  // PhotoLightbox is an image viewer; a PDF opens in a new tab from its own tile.
  const imagePhotos = photos.filter((p) => !isPdfPhoto(p));

  return (
    <div className="border-t border-gray-100 first:border-t-0">
      <div className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50/50 transition-colors">
        {/* Collector name — clickable to expand kringen */}
        <button
          type="button"
          onClick={() => hasKringen && setExpanded(!expanded)}
          className={`flex-1 text-left text-sm font-medium text-td-navy ${hasKringen ? 'cursor-pointer hover:text-td-blue' : 'cursor-default'}`}
        >
          <span className="flex items-center gap-2">
            {collector.name}
            {hasKringen && (
              <svg
                className={`w-3.5 h-3.5 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            )}
            <span className="text-xs text-gray-400">({kringenCount} kringen)</span>
          </span>
        </button>

        {/* Systeem badge */}
        {collector.x_studio_collector_systeem && (
          <span className="hidden sm:inline text-xs font-semibold bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full whitespace-nowrap">
            {collector.x_studio_collector_systeem}
          </span>
        )}

        {/* m2 */}
        <span className="text-xs text-gray-500 whitespace-nowrap">
          {collector.x_studio_collector_m2 || 0} m&sup2;
        </span>

        {/* Blok */}
        {blokName && (
          <span className="hidden sm:inline text-xs text-gray-400 whitespace-nowrap">
            Blok {blokName}
          </span>
        )}

        {/* Op druk indicator with date */}
        {collector.x_studio_collector_op_druk && (
          <span className="hidden sm:inline text-xs font-semibold bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full whitespace-nowrap">
            Op druk{collector.x_studio_collector_op_druk_datum
              ? ` ${collector.x_studio_collector_op_druk_datum.slice(8, 10)}-${collector.x_studio_collector_op_druk_datum.slice(5, 7)}-${collector.x_studio_collector_op_druk_datum.slice(0, 4)}`
              : ''}
          </span>
        )}

        {/* PO status badge */}
        {hasPo && (
          <span className="text-xs font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded-full whitespace-nowrap" title={poEntry.orderName}>
            {poEntry.orderName}
          </span>
        )}
        {!hasPo && poStatusLoading && (
          <span className="text-xs text-gray-300 whitespace-nowrap">...</span>
        )}
        {!hasPo && !poStatusLoading && (
          <span className="text-xs font-semibold bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full whitespace-nowrap">
            Geen PO
          </span>
        )}
      </div>

      {/* Expanded content: kringen + photos */}
      {expanded && (
        <div className="px-4 pb-3 pl-12">
          {/* Kringen table */}
          {hasKringen && collector.kringen && (
            <table className="w-full text-xs mb-4">
              <thead>
                <tr className="text-left text-td-muted uppercase tracking-wide">
                  <th className="py-1.5 pr-4 font-semibold">Kringnummer</th>
                  <th className="py-1.5 pr-4 font-semibold">Systeem</th>
                  <th className="py-1.5 pr-4 font-semibold">Legpatroon (cm)</th>
                  <th className="py-1.5 font-semibold">Lengte (m)</th>
                </tr>
              </thead>
              <tbody>
                {collector.kringen.map((kring) => (
                  <tr key={kring.id} className="border-t border-gray-50">
                    <td className="py-1.5 pr-4 text-td-navy font-medium">{kring.x_kringnummer}</td>
                    <td className="py-1.5 pr-4 text-gray-600">{kring.x_system || '-'}</td>
                    <td className="py-1.5 pr-4 text-gray-600">{kring.x_legpatroon || '-'}</td>
                    <td className="py-1.5 text-gray-600">{kring.x_length || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Photos */}
          {photosLoading && (
            <div className="flex items-center gap-2 py-2">
              <div className="w-4 h-4 border-2 border-td-blue/30 border-t-td-blue rounded-full animate-spin" />
              <span className="text-xs text-td-muted">Foto's laden...</span>
            </div>
          )}
          {!photosLoading && photos.length > 0 && (
            <div>
              <p className="text-xs font-bold text-td-muted uppercase tracking-wide mb-2">
                Foto's ({photos.length})
              </p>
              <div className="flex flex-wrap gap-2">
                {photos.map((p) => (
                  <PhotoGridThumb
                    key={p.key}
                    photo={p}
                    tileSizeClass="w-20 h-20"
                    imgClassName="block w-20 h-20 rounded-lg object-cover border border-gray-200 hover:border-td-blue transition-colors cursor-pointer"
                    onOpen={() => { setLightboxIndex(imagePhotos.findIndex((i) => i.key === p.key)); setLightboxOpen(true); }}
                  />
                ))}
              </div>
            </div>
          )}
          {!photosLoading && photos.length === 0 && !hasKringen && (
            <p className="text-xs text-gray-400">Geen kringen of foto's.</p>
          )}
        </div>
      )}

      {/* Lightbox */}
      {lightboxOpen && photos.length > 0 && (
        <PhotoLightbox
          photos={imagePhotos}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
}
